import React, { FC, useCallback, useState } from 'react'
import { Stack, TextField, InputAdornment, IconButton, CircularProgress, Tooltip } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { exists, objectMap } from '../../../utils'
import Icon from '@iconify/react'
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";

export type NewPasswordState = {
  password: string,
  confirmPassword: string
}

type NewPasswordProps = {
  onSubmit: (values: NewPasswordState) => void,
  loading: boolean
}

const NewPasswordForm: FC<NewPasswordProps> = ({ onSubmit, loading }) => {

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const schema = yup.object().shape({
    password: yup.string()
      .required('Senha obrigatória')
      .min(6, 'A senha deve ter no mínimo 6 dígitos')
      .matches(/([a-z])+/g, 'Digite ao menos uma letra minúscula')
      .matches(/([A-Z])+/g, 'Digite ao menos uma letra maiúscula')
      .matches(/([0-9])+/g, 'Digite ao menos um dígito numérico')
      .matches(/([!@#$%¨^&*()\-[\]{}+=/;:'"._])+/g, 'Digite ao menos um símbolo ex. @#!'),
    confirmPassword: yup.string()
      .required('Senha obrigatória')
      .oneOf([yup.ref('password'), null], 'As senhas devem ser iguais')
  })

  const { getFieldProps, errors, touched, values, validateForm, setTouched } = useFormik<NewPasswordState>({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: schema,
    onSubmit: () => {},
    validateOnChange: true,
    validateOnBlur: true,
  })

  const onSubmitCallback = useCallback(async () => {
    const errs = await validateForm()
    setTouched(objectMap(values, () => true))
    if (!exists(errs)) {
      onSubmit(values)
    }
  }, [values, setTouched, validateForm, onSubmit])

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        type={showPassword ? 'text' : 'password'}
        label="Nova senha"
        {...getFieldProps('password')}
        error={!!errors.password && touched.password}
        helperText={touched.password ? (errors.password ?? ' ') : ' '}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={showPassword ? "Ocultar senha" : "Mostrar senha"} placement="top">
                <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                  <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        fullWidth
        type={showConfirm ? 'text' : 'password'}
        label="Confirmar senha"
        {...getFieldProps('confirmPassword')}
        error={!!errors.confirmPassword && touched.confirmPassword}
        helperText={touched.confirmPassword ? (errors.confirmPassword ?? ' ') : ' '}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={showConfirm ? "Ocultar senha" : "Mostrar senha"} placement="top">
                <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end">
                  <Icon icon={showConfirm ? eyeFill : eyeOffFill} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <LoadingButton
        fullWidth
        size="large"
        variant="contained"
        color="primary"
        onClick={onSubmitCallback}
        loading={loading}
        loadingIndicator={<CircularProgress color="inherit" size={18} />}
        sx={{ height: 54, fontSize: '15px', fontWeight: 600 }}
      >
        Alterar senha
      </LoadingButton>
    </Stack>
  )
}

export default NewPasswordForm
